import type { TFunction } from 'i18next';
import { fetchWithSession } from './fetchWithSession';
// Base URLs from environment variables
import {
  createCombinedSummary,
  getFileTypeFromMimeType,
} from './fileService.helpers';
import type {
  FileInfo,
  FileMetadata,
  FileSearchFilters,
  FileSummary,
  MultiFileSummaryResponse,
} from './fileService.types';

export type {
  FileInfo,
  FileMetadata,
  FileSearchFilters,
  FileSummary,
  MultiFileSummaryResponse,
} from './fileService.types';

const API_URL = process.env.REACT_APP_API_URL || '';

class FileService {
  /**
   * Get access token from sessionStorage
   */
  private getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Get headers with Authorization token
   * Cookie認証廃止により、Authorization headerのみを使用
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get list of available files for selection with optional filters
   */
  async getFilesForSelection(filters?: FileSearchFilters): Promise<FileInfo[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.categories?.length) {
        params.set('categories', filters.categories.join(','));
      }
      if (filters?.tags?.length) {
        params.set('tags', filters.tags.join(','));
      }
      if (filters?.fileTypes?.length) {
        params.set('file_types', filters.fileTypes.join(','));
      }

      const queryString = params.toString();
      const url = queryString ? `${API_URL}/files/list?${queryString}` : `${API_URL}/files/list`;

      const response = await fetchWithSession(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const files: FileInfo[] = await response.json();

      // Map the file type based on mimeType if needed
      return files.map(file => ({
        ...file,
        type: getFileTypeFromMimeType(file.mimeType || 'application/octet-stream') as 'file' | 'document' | 'code',
      }));
    } catch (error) {
      console.error('Failed to fetch files for selection:', error);
      throw error;
    }
  }

  /**
   * Get summary for a specific file
   */
  async getFileSummary(fileId: string, deep = false): Promise<string> {
    try {
      const url = `${API_URL}/files/${fileId}/summary${deep ? '?deep=true' : ''}`;

      const response = await fetchWithSession(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch file summary');
      }

      const result: FileSummary = await response.json();
      return result.summary;
    } catch (error) {
      console.error(`Failed to fetch summary for file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Get summaries for multiple files
   * This is a convenience method that calls getFileSummary for each file
   */
  async getMultipleFileSummaries(
    fileIds: string[],
    t: TFunction,
    includeIndividual = true,
    includeCombined = true,
    useDeepAnalysis = false
  ): Promise<MultiFileSummaryResponse> {
    try {
      const result: MultiFileSummaryResponse = {};

      // Get individual summaries
      if (includeIndividual) {
        const summaryPromises = fileIds.map(async (fileId) => {
          try {
            const summary = await this.getFileSummary(fileId, useDeepAnalysis);
            return { fileId, summary };
          } catch (error) {
            console.error(`Failed to get summary for file ${fileId}:`, error);
            return { fileId, summary: '' };
          }
        });

        const summaries = await Promise.all(summaryPromises);
        result.individualSummaries = {};
        summaries.forEach(({ fileId, summary }) => {
          if (result.individualSummaries) {
            result.individualSummaries[fileId] = summary;
          }
        });
      }

      // Create combined summary if requested
      if (includeCombined && result.individualSummaries) {
        const allSummaries = Object.values(result.individualSummaries).filter(Boolean);
        if (allSummaries.length > 0) {
          result.combinedSummary = createCombinedSummary(allSummaries, fileIds, t, useDeepAnalysis);
        }
      }

      return result;
    } catch (error) {
      console.error('Failed to get multiple file summaries:', error);
      throw error;
    }
  }

  /**
   * Search files by name or content with optional filters
   */
  async searchFiles(query: string, limit = 10, filters?: FileSearchFilters): Promise<FileInfo[]> {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(limit),
      });
      if (filters?.categories?.length) {
        params.set('categories', filters.categories.join(','));
      }
      if (filters?.tags?.length) {
        params.set('tags', filters.tags.join(','));
      }
      if (filters?.fileTypes?.length) {
        params.set('file_types', filters.fileTypes.join(','));
      }

      const response = await fetchWithSession(
        `${API_URL}/files/search?${params}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        // If search fails, fall back to getting all files and filtering locally
        console.warn('File search API not available, using local filtering');
        const allFiles = await this.getFilesForSelection(filters);
        return allFiles.filter(file =>
          file.name.toLowerCase().includes(query.toLowerCase()) ||
          file.title.toLowerCase().includes(query.toLowerCase()) ||
          (file.description && file.description.toLowerCase().includes(query.toLowerCase()))
        ).slice(0, limit);
      }

      const searchResults = await response.json();
      return searchResults.map((result: any) => ({
        id: result.id,
        name: result.file_name || result.fileName,
        title: result.title,
        description: result.metadata?.summary || '',
        path: result.file_path || '',
        type: getFileTypeFromMimeType(result.metadata?.mimeType || 'application/octet-stream') as 'file' | 'document' | 'code',
        category: result.category,
        tags: result.tags || [],
      }));
    } catch (error) {
      console.error('Failed to search files:', error);
      // Return empty array instead of throwing to allow graceful degradation
      return [];
    }
  }

  /**
   * Get available metadata values for filter UI (categories, tags, file types)
   */
  async getFileMetadata(): Promise<FileMetadata> {
    try {
      const response = await fetchWithSession(`${API_URL}/files/metadata`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch file metadata');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch file metadata:', error);
      return { categories: [], tags: [], fileTypes: [] };
    }
  }
}

export const fileService = new FileService();
export default fileService;
